import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  DiscountConfig,
  MarkupConfig,
  SalesOrder,
  ExchangeFormData,
  ExchangeStep,
  ExchangeSummary,
  SelectedProduct,
  PaymentEntry,
} from '../../../../types/sales.types';
import { salesService } from '../../../../services/sales.service';
import { WarehouseInventoryProduct } from '../../../../types/sales.types';
import { formatCurrency as formatCurrencyValue } from '../../../../utils/formatters/money';
import { roundMoney } from '../../../../utils/money';
import {
  clampExchangeUniformDiscountValue,
  computeExchangeProductFinalPrice,
  computeOriginalItemsTotal,
  computePaidForExchangedItems,
  isExchangeDiscountEnabled,
  isExchangeSummaryInvalid,
  resolveExchangeLinePrices,
} from './exchangePricing.utils';
import { buildExchangeSummary } from './exchangeSummary.utils';
import { createPaymentEntryId } from '../../../../utils/sales/payment.utils';
import { extractErrorMessage } from '../../../../utils/errorHelpers';
import {
  createInitialExchangeDiscountConfig,
  createInitialExchangeFormData,
  createInitialExchangeMarkupConfig,
} from './exchangeForm.constants';
import {
  applyUniformDiscountToLines,
  applyUniformMarkupToLines,
  buildDefaultLinePricingFields,
  buildLinePricingFields,
  calculateAdjustedPrice,
  normalizeAdjustmentType,
  type LineAdjustmentType,
} from '../../../../utils/sales/line-pricing.utils';
import { useExchangeEligibility } from './useExchangeEligibility';
import { ExchangeStep2FlowPhase } from './ExchangeStep2Payment.hooks';
import { isRemoteCartItem } from '../../../../utils/sales/mixed-sale.utils';
import { useOrdersPermissions } from '../../../../hooks/useOrdersPermissions';
import type { CommercialDateMode } from '../../../../utils/sales/commercial-sale-date';
import { commercialDateOverrideIso } from '../../../../utils/sales/commercial-sale-date';
import { isValid } from 'date-fns';

function toDistributionLines(products: SelectedProduct[]) {
  return products.map((product) => ({
    id: product.warehouseInventoryProduct.id,
    originalPrice: product.originalPrice,
    markupType: product.markupType,
    markupValue: product.markupValue,
  }));
}

function mapExchangeProductsWithUniformMarkup(
  products: SelectedProduct[],
  settings: { markupType: 'PERCENTAGE' | 'AMOUNT'; markupValue: number },
): SelectedProduct[] {
  if (products.length === 0) {
    return products;
  }

  const fieldsByLineId = applyUniformMarkupToLines(toDistributionLines(products), settings);

  return products.map((product) => {
    const markupFields = fieldsByLineId.get(product.warehouseInventoryProduct.id);
    if (markupFields == null) {
      return product;
    }
    return {
      ...product,
      ...buildLinePricingFields(
        product.originalPrice,
        markupFields.markupType,
        markupFields.markupValue,
        'NONE',
        0,
      ),
    };
  });
}

function mapExchangeProductsWithUniformDiscount(
  products: SelectedProduct[],
  settings: { discountType: 'PERCENTAGE' | 'AMOUNT'; discountValue: number },
): SelectedProduct[] {
  if (products.length === 0) {
    return products;
  }

  const fieldsByLineId = applyUniformDiscountToLines(toDistributionLines(products), settings);

  return products.map((product) => {
    const discountFields = fieldsByLineId.get(product.warehouseInventoryProduct.id);
    if (discountFields == null) {
      return product;
    }
    const adjustedPrice = calculateAdjustedPrice(
      product.originalPrice,
      product.markupType,
      product.markupValue,
    );
    return {
      ...product,
      adjustedPrice,
      ...discountFields,
    };
  });
}

function applyExchangeFinalPrices(products: SelectedProduct[], floor: number): SelectedProduct[] {
  const discountEnabled = isExchangeDiscountEnabled(products);
  const prices = resolveExchangeLinePrices(products, discountEnabled, floor);
  return products.map((product) => ({
    ...product,
    finalPrice:
      prices.get(product.warehouseInventoryProduct.id) ??
      computeExchangeProductFinalPrice(product, products, discountEnabled, floor),
  }));
}

function shouldApplyUniformMarkup(markupConfig: MarkupConfig | undefined): boolean {
  return Boolean(
    markupConfig?.enabled &&
    !markupConfig.allowIndividualMarkups &&
    markupConfig.orderMarkupValue > 0,
  );
}

function shouldApplyUniformDiscount(discountConfig: DiscountConfig | undefined): boolean {
  return Boolean(
    discountConfig?.enabled &&
    !discountConfig.allowIndividualDiscounts &&
    discountConfig.orderDiscountValue > 0,
  );
}

function getDiscountConfigOrDefault(config?: DiscountConfig): DiscountConfig {
  return config ?? createInitialExchangeDiscountConfig();
}

function getMarkupConfigOrDefault(config?: MarkupConfig): MarkupConfig {
  return config ?? createInitialExchangeMarkupConfig();
}

export interface UseExchangeModalProps {
  open: boolean;
  order: SalesOrder | null;
  onExchangeCompleted?: () => void;
}

export const useExchangeModal = ({ open, order, onExchangeCompleted }: UseExchangeModalProps) => {
  const [currentStep, setCurrentStep] = useState<ExchangeStep>('SELECTION');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [paymentFlowPhase, setPaymentFlowPhase] = useState<ExchangeStep2FlowPhase>(1);
  const [paymentsLocked, setPaymentsLocked] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [exchangeDateMode, setExchangeDateMode] = useState<CommercialDateMode | null>(null);
  const [exchangeDate, setExchangeDate] = useState<Date>(() => new Date());
  const [exchangeDateModeError, setExchangeDateModeError] = useState(false);
  const { canModifySaleDate } = useOrdersPermissions();

  const {
    eligibility,
    isLoadingEligibility,
    eligibilityError,
    loadEligibility,
    clearEligibilityError,
  } = useExchangeEligibility(order);

  const [exchangeData, setExchangeData] = useState<ExchangeFormData>(() =>
    createInitialExchangeFormData(0),
  );

  useEffect(() => {
    if (open && order) {
      void loadEligibility();
      setExchangeData(createInitialExchangeFormData(order.id));
      setCurrentStep('SELECTION');
      setPaymentFlowPhase(1);
      setPaymentsLocked(false);
      setShowConfirmModal(false);
      setSubmitError(null);
      setExchangeDateMode(null);
      setExchangeDate(new Date());
      setExchangeDateModeError(false);
    }
  }, [open, order, loadEligibility]);

  const isLoading = isLoadingEligibility || isSubmitting;
  const error = submitError ?? eligibilityError;

  const paidFloor = useMemo(() => {
    if (!order) return 0;
    const originalItemsTotal = computeOriginalItemsTotal(order, exchangeData.itemsToExchange);
    return computePaidForExchangedItems(order, originalItemsTotal);
  }, [order, exchangeData.itemsToExchange]);

  const resolvedLinePrices = useMemo(
    () =>
      resolveExchangeLinePrices(
        exchangeData.newProducts,
        isExchangeDiscountEnabled(exchangeData.newProducts),
        paidFloor,
      ),
    [exchangeData.newProducts, paidFloor],
  );

  const getProductFinalPrice = useCallback(
    (product: SelectedProduct): number =>
      resolvedLinePrices.get(product.warehouseInventoryProduct.id) ??
      roundMoney(product.originalPrice),
    [resolvedLinePrices],
  );

  const exchangeSummary = useMemo(
    () => buildExchangeSummary(order, exchangeData, getProductFinalPrice),
    [order, exchangeData, getProductFinalPrice],
  );

  const meetsExchangeCreditRule = useMemo(
    () =>
      !isExchangeSummaryInvalid(
        exchangeSummary.newItemsTotal,
        exchangeSummary.paidForExchangedItems,
        exchangeSummary.isSummaryCoherent,
      ),
    [
      exchangeSummary.newItemsTotal,
      exchangeSummary.paidForExchangedItems,
      exchangeSummary.isSummaryCoherent,
    ],
  );

  const requiresZeroSaleAck = useMemo(
    () =>
      exchangeData.newProducts.some((product) => roundMoney(getProductFinalPrice(product)) === 0),
    [exchangeData.newProducts, getProductFinalPrice],
  );

  const canProceedToNextStep = useMemo(() => {
    const hasExchangeSelection =
      exchangeData.itemsToExchange.length > 0 && exchangeData.newProducts.length > 0;

    if (
      !hasExchangeSelection ||
      !Number.isFinite(exchangeSummary.newItemsTotal) ||
      !meetsExchangeCreditRule
    ) {
      return false;
    }

    switch (currentStep) {
      case 'SELECTION':
        return true;
      case 'PAYMENT':
        if (paymentFlowPhase < 4) {
          return false;
        }
        if (!exchangeSummary.requiresPayment) {
          return true;
        }
        {
          const allowPartial = exchangeData.paymentConfig?.allowPartialPayment ?? false;
          if (allowPartial) {
            return (
              (exchangeData.payments?.length ?? 0) > 0 &&
              exchangeData.payments?.every((p) => p.amount > 0) &&
              exchangeSummary.totalPayments > 0
            );
          }
          return exchangeSummary.totalPayments >= exchangeSummary.difference;
        }
      case 'CONFIRMATION':
        return true;
      default:
        return false;
    }
  }, [currentStep, exchangeData, exchangeSummary, paymentFlowPhase, meetsExchangeCreditRule]);

  const clearExchangePayments = useCallback(() => {
    setExchangeData((prev) => ({
      ...prev,
      payments: [],
    }));
  }, []);

  const clearNewProducts = useCallback(() => {
    setExchangeData((prev) => ({
      ...prev,
      newProducts: [],
      payments: [],
    }));
  }, []);

  const handleSelectItemsToExchange = useCallback(
    (itemIds: number[]) => {
      setExchangeData((prev) => {
        if (!order || prev.newProducts.length === 0) {
          return { ...prev, itemsToExchange: itemIds };
        }
        const originalItemsTotal = computeOriginalItemsTotal(order, itemIds);
        const floor = computePaidForExchangedItems(order, originalItemsTotal);
        return {
          ...prev,
          itemsToExchange: itemIds,
          newProducts: applyExchangeFinalPrices(prev.newProducts, floor),
        };
      });
    },
    [order],
  );

  const reapplyPricingAfterProductChange = useCallback(
    (prev: ExchangeFormData, products: SelectedProduct[]): SelectedProduct[] => {
      if (!order) return products;

      let nextProducts = products;
      const markupConfig = getMarkupConfigOrDefault(prev.markupConfig);
      const discountConfig = getDiscountConfigOrDefault(prev.discountConfig);

      if (shouldApplyUniformMarkup(markupConfig)) {
        nextProducts = mapExchangeProductsWithUniformMarkup(nextProducts, {
          markupType: markupConfig.orderMarkupType,
          markupValue: markupConfig.orderMarkupValue,
        });
      }

      if (shouldApplyUniformDiscount(discountConfig)) {
        nextProducts = mapExchangeProductsWithUniformDiscount(nextProducts, {
          discountType: discountConfig.orderDiscountType,
          discountValue: discountConfig.orderDiscountValue,
        });
      }

      const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
      const floor = computePaidForExchangedItems(order, originalItemsTotal);
      return applyExchangeFinalPrices(nextProducts, floor);
    },
    [order],
  );

  const handleAddNewProduct = useCallback(
    (product: WarehouseInventoryProduct) => {
      const price = roundMoney(product.salePrice ?? product.product?.salePrice ?? 0);

      setExchangeData((prev) => {
        const selectedProduct: SelectedProduct = {
          warehouseInventoryProduct: product,
          originalPrice: price,
          ...buildDefaultLinePricingFields(price),
          isDelivered: false,
        };

        const newProducts = reapplyPricingAfterProductChange(prev, [
          ...prev.newProducts,
          selectedProduct,
        ]);

        return { ...prev, newProducts };
      });
    },
    [reapplyPricingAfterProductChange],
  );

  const handleRemoveNewProduct = useCallback(
    (productId: number) => {
      setExchangeData((prev) => {
        const filtered = prev.newProducts.filter(
          (p) => p.warehouseInventoryProduct.id !== productId,
        );
        return {
          ...prev,
          newProducts: reapplyPricingAfterProductChange(prev, filtered),
        };
      });
    },
    [reapplyPricingAfterProductChange],
  );

  const applyLinePricingWithFloor = useCallback(
    (
      prev: ExchangeFormData,
      productId: number,
      markupType: LineAdjustmentType,
      markupValue: number,
      discountType: LineAdjustmentType,
      discountValue: number,
    ): SelectedProduct[] => {
      if (!order) return prev.newProducts;

      const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
      const floor = computePaidForExchangedItems(order, originalItemsTotal);

      const updatedProducts = prev.newProducts.map((product) => {
        if (product.warehouseInventoryProduct.id !== productId) {
          return product;
        }
        return {
          ...product,
          ...buildLinePricingFields(
            product.originalPrice,
            markupType,
            markupValue,
            discountType,
            discountValue,
          ),
        };
      });

      return applyExchangeFinalPrices(updatedProducts, floor);
    },
    [order],
  );

  const handleUpdateNewProduct = useCallback(
    (productId: number, updates: Partial<SelectedProduct>) => {
      if (!order) return;

      setExchangeData((prev) => {
        const target = prev.newProducts.find((p) => p.warehouseInventoryProduct.id === productId);
        if (!target) return prev;

        const markupType = updates.markupType ?? target.markupType;
        const markupValue =
          updates.markupValue !== undefined ? updates.markupValue : target.markupValue;
        const discountType = updates.discountType ?? target.discountType;
        const discountValue =
          updates.discountValue !== undefined ? updates.discountValue : target.discountValue;

        if (
          updates.markupType !== undefined ||
          updates.markupValue !== undefined ||
          updates.discountType !== undefined ||
          updates.discountValue !== undefined
        ) {
          return {
            ...prev,
            newProducts: applyLinePricingWithFloor(
              prev,
              productId,
              markupType,
              markupValue,
              discountType,
              discountValue,
            ),
          };
        }

        return {
          ...prev,
          newProducts: prev.newProducts.map((p) =>
            p.warehouseInventoryProduct.id === productId ? { ...p, ...updates } : p,
          ),
        };
      });
    },
    [order, applyLinePricingWithFloor],
  );

  const updateNewProductMarkup = useCallback(
    (productId: number, markupType: LineAdjustmentType, markupValue: number) => {
      if (!order) return;

      setExchangeData((prev) => {
        const target = prev.newProducts.find((p) => p.warehouseInventoryProduct.id === productId);
        if (!target) return prev;

        return {
          ...prev,
          newProducts: applyLinePricingWithFloor(
            prev,
            productId,
            markupType,
            roundMoney(Math.max(0, markupValue)),
            target.discountType,
            target.discountValue,
          ),
        };
      });
    },
    [order, applyLinePricingWithFloor],
  );

  const updateNewProductDiscount = useCallback(
    (productId: number, discountType: LineAdjustmentType, discountValue: number) => {
      if (!order) return;

      setExchangeData((prev) => {
        const target = prev.newProducts.find((p) => p.warehouseInventoryProduct.id === productId);
        if (!target) return prev;

        return {
          ...prev,
          newProducts: applyLinePricingWithFloor(
            prev,
            productId,
            target.markupType,
            target.markupValue,
            discountType,
            roundMoney(Math.max(0, discountValue)),
          ),
        };
      });
    },
    [order, applyLinePricingWithFloor],
  );

  const resetAllLinePricing = useCallback(() => {
    setExchangeData((prev) => ({
      ...prev,
      markupConfig: createInitialExchangeMarkupConfig(),
      discountConfig: createInitialExchangeDiscountConfig(),
      newProducts: prev.newProducts.map((product) => ({
        ...product,
        ...buildDefaultLinePricingFields(product.originalPrice),
      })),
    }));
  }, []);

  const commitCommercialPricingConfig = useCallback(
    (draft: {
      markupEnabled: boolean;
      allowIndividualMarkups: boolean;
      markupType: 'PERCENTAGE' | 'AMOUNT';
      markupValue: number;
      discountEnabled: boolean;
      allowIndividualDiscounts: boolean;
      discountType: 'PERCENTAGE' | 'AMOUNT';
      discountValue: number;
    }) => {
      if (!order) return;

      setExchangeData((prev) => {
        const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
        const floor = computePaidForExchangedItems(order, originalItemsTotal);
        const markupType = normalizeAdjustmentType(draft.markupType);
        const discountType = normalizeAdjustmentType(draft.discountType);

        let newProducts = prev.newProducts.map((product) => ({
          ...product,
          ...buildDefaultLinePricingFields(product.originalPrice),
        }));

        const markupConfig: MarkupConfig = {
          ...createInitialExchangeMarkupConfig(),
          enabled: draft.markupEnabled,
          allowIndividualMarkups: draft.allowIndividualMarkups,
          orderMarkupType: markupType,
          orderMarkupValue: roundMoney(Math.max(0, draft.markupValue)),
          scope: markupType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
        };

        const discountConfig: DiscountConfig = {
          ...createInitialExchangeDiscountConfig(),
          enabled: draft.discountEnabled,
          allowIndividualDiscounts: draft.allowIndividualDiscounts,
          orderDiscountType: discountType,
          orderDiscountValue: roundMoney(Math.max(0, draft.discountValue)),
          scope: discountType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
        };

        if (
          markupConfig.enabled &&
          !markupConfig.allowIndividualMarkups &&
          markupConfig.orderMarkupValue > 0
        ) {
          newProducts = mapExchangeProductsWithUniformMarkup(newProducts, {
            markupType: markupConfig.orderMarkupType,
            markupValue: markupConfig.orderMarkupValue,
          });
        }

        if (
          discountConfig.enabled &&
          !discountConfig.allowIndividualDiscounts &&
          discountConfig.orderDiscountValue > 0
        ) {
          const validatedValue = clampExchangeUniformDiscountValue(
            discountConfig.orderDiscountType,
            discountConfig.orderDiscountValue,
            newProducts,
            floor,
          );
          discountConfig.orderDiscountValue = validatedValue;
          newProducts = mapExchangeProductsWithUniformDiscount(newProducts, {
            discountType: discountConfig.orderDiscountType,
            discountValue: validatedValue,
          });
        }

        return {
          ...prev,
          markupConfig,
          discountConfig,
          newProducts: applyExchangeFinalPrices(newProducts, floor),
        };
      });
    },
    [order],
  );

  const updateMarkupConfig = useCallback((config: Partial<MarkupConfig>) => {
    setExchangeData((prev) => ({
      ...prev,
      markupConfig: {
        ...getMarkupConfigOrDefault(prev.markupConfig),
        ...config,
      },
    }));
  }, []);

  const updateDiscountConfig = useCallback((config: Partial<DiscountConfig>) => {
    setExchangeData((prev) => ({
      ...prev,
      discountConfig: {
        ...getDiscountConfigOrDefault(prev.discountConfig),
        ...config,
      },
    }));
  }, []);

  const applyUniformMarkupToNewProducts = useCallback(
    (markupType: 'PERCENTAGE' | 'AMOUNT', markupValue: number) => {
      if (!order) return;

      setExchangeData((prev) => {
        const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
        const floor = computePaidForExchangedItems(order, originalItemsTotal);
        const normalizedMarkupValue = roundMoney(Math.max(0, markupValue));

        let productsWithMarkup = mapExchangeProductsWithUniformMarkup(prev.newProducts, {
          markupType,
          markupValue: normalizedMarkupValue,
        });

        const discountConfig = getDiscountConfigOrDefault(prev.discountConfig);
        if (shouldApplyUniformDiscount(discountConfig)) {
          productsWithMarkup = mapExchangeProductsWithUniformDiscount(productsWithMarkup, {
            discountType: discountConfig.orderDiscountType,
            discountValue: discountConfig.orderDiscountValue,
          });
        }

        return {
          ...prev,
          markupConfig: {
            ...getMarkupConfigOrDefault(prev.markupConfig),
            orderMarkupType: markupType,
            orderMarkupValue: normalizedMarkupValue,
            scope: markupType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
          },
          newProducts: applyExchangeFinalPrices(productsWithMarkup, floor),
        };
      });
    },
    [order],
  );

  const applyUniformDiscountToNewProducts = useCallback(
    (discountType: 'PERCENTAGE' | 'AMOUNT', discountValue: number) => {
      if (!order) return;

      setExchangeData((prev) => {
        const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
        const floor = computePaidForExchangedItems(order, originalItemsTotal);
        const validatedValue = clampExchangeUniformDiscountValue(
          discountType,
          discountValue,
          prev.newProducts,
          floor,
        );
        const settings = {
          discountType,
          discountValue: validatedValue,
        };

        const productsWithDiscount = mapExchangeProductsWithUniformDiscount(
          prev.newProducts,
          settings,
        );

        return {
          ...prev,
          discountConfig: {
            ...getDiscountConfigOrDefault(prev.discountConfig),
            orderDiscountType: discountType,
            orderDiscountValue: validatedValue,
            scope: discountType === 'AMOUNT' ? 'PER_ORDER' : 'PER_ITEM',
          },
          newProducts: applyExchangeFinalPrices(productsWithDiscount, floor),
        };
      });
    },
    [order],
  );

  const handleAddPayment = useCallback(
    (
      method: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      const payment: PaymentEntry = {
        id: createPaymentEntryId(),
        method,
        amount: roundMoney(amount),
        cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
        paymentNotes: notes,
        bankReference,
        qrTransactionId,
      };

      setExchangeData((prev) => ({
        ...prev,
        payments: [...(prev.payments || []), payment],
      }));
    },
    [],
  );

  const handleRemovePayment = useCallback((paymentId: string) => {
    setExchangeData((prev) => ({
      ...prev,
      payments: prev.payments?.filter((p) => p.id !== paymentId) || [],
    }));
  }, []);

  const handleUpdatePayment = useCallback(
    (
      paymentId: string,
      amount: number,
      cashReceived?: number,
      notes?: string,
      bankReference?: string,
      qrTransactionId?: string,
    ) => {
      setExchangeData((prev) => ({
        ...prev,
        payments:
          prev.payments?.map((p) =>
            p.id === paymentId
              ? {
                  ...p,
                  amount: roundMoney(amount),
                  cashReceived: cashReceived != null ? roundMoney(cashReceived) : undefined,
                  paymentNotes: notes,
                  bankReference,
                  qrTransactionId,
                }
              : p,
          ) || [],
      }));
    },
    [],
  );

  const setProductDelivered = useCallback((productId: number, isDelivered: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      newProducts: prev.newProducts.map((product) =>
        product.warehouseInventoryProduct.id === productId ? { ...product, isDelivered } : product,
      ),
    }));
  }, []);

  const applyDeliveryDefaults = useCallback(
    (paymentStatus: ExchangeSummary['paymentStatus'], sellingLocationId: number | undefined) => {
      setExchangeData((prev) => ({
        ...prev,
        newProducts: prev.newProducts.map((product) => {
          const isRemote = isRemoteCartItem(product, sellingLocationId);
          if (isRemote) {
            return { ...product, isDelivered: false };
          }
          return {
            ...product,
            isDelivered: paymentStatus === 'complete',
          };
        }),
      }));
    },
    [],
  );

  const handleNextStep = useCallback(() => {
    if (!meetsExchangeCreditRule) {
      return;
    }

    if (currentStep === 'SELECTION') {
      setPaymentFlowPhase(1);
      setPaymentsLocked(false);
      setCurrentStep('PAYMENT');
    } else if (currentStep === 'PAYMENT') {
      setPaymentsLocked(true);
      applyDeliveryDefaults(exchangeSummary.paymentStatus, order?.location?.id);
      setCurrentStep('CONFIRMATION');
    }
  }, [
    currentStep,
    meetsExchangeCreditRule,
    applyDeliveryDefaults,
    exchangeSummary.paymentStatus,
    order?.location?.id,
  ]);

  const handlePreviousStep = useCallback(() => {
    if (currentStep === 'PAYMENT') {
      setCurrentStep('SELECTION');
    } else if (currentStep === 'CONFIRMATION') {
      setCurrentStep('PAYMENT');
      setPaymentFlowPhase(4);
      setPaymentsLocked(true);
    }
  }, [currentStep]);

  const handleExchangeDateModeChange = useCallback((mode: CommercialDateMode) => {
    setExchangeDateMode(mode);
    setExchangeDate(new Date());
    setExchangeDateModeError(false);
  }, []);

  const openConfirmModal = useCallback(() => {
    if (!meetsExchangeCreditRule || isSubmitting) return;
    if (canModifySaleDate && exchangeDateMode == null) {
      setExchangeDateModeError(true);
      return;
    }
    if (canModifySaleDate && exchangeDateMode === 'custom' && !isValid(exchangeDate)) {
      setSubmitError('Seleccione una fecha y hora de intercambio válida.');
      return;
    }
    setExchangeDateModeError(false);
    setSubmitError(null);
    setShowConfirmModal(true);
  }, [meetsExchangeCreditRule, isSubmitting, canModifySaleDate, exchangeDateMode, exchangeDate]);

  const closeConfirmModal = useCallback(() => {
    if (isSubmitting) return;
    setShowConfirmModal(false);
  }, [isSubmitting]);

  const handleConfirmExchange = useCallback(async () => {
    if (submittingRef.current || isSubmitting) return;
    if (!order || !meetsExchangeCreditRule) return;
    if (canModifySaleDate && exchangeDateMode == null) {
      setExchangeDateModeError(true);
      setShowConfirmModal(false);
      return;
    }
    if (canModifySaleDate && exchangeDateMode === 'custom' && !isValid(exchangeDate)) {
      setSubmitError('Seleccione una fecha y hora de intercambio válida.');
      setShowConfirmModal(false);
      return;
    }

    submittingRef.current = true;
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const exchangeDateOverride =
        canModifySaleDate && exchangeDateMode != null
          ? commercialDateOverrideIso(exchangeDateMode, exchangeDate)
          : undefined;

      const exchangePayload = {
        itemsToExchange: exchangeData.itemsToExchange,
        newProducts: exchangeData.newProducts.map((product) => ({
          warehouseInventoryProductId: product.warehouseInventoryProduct.id,
          originalPrice: roundMoney(product.originalPrice),
          markupType: product.markupType,
          markupValue: roundMoney(product.markupValue || 0),
          adjustedPrice: roundMoney(product.adjustedPrice),
          discountType: product.discountType,
          discountValue: roundMoney(product.discountValue || 0),
          finalPrice: roundMoney(getProductFinalPrice(product)),
          isDelivered: Boolean(product.isDelivered),
        })),
        payments: exchangeData.payments?.map((payment) => ({
          paymentMethod: payment.method,
          paymentAmount: payment.amount,
          cashReceived: payment.cashReceived,
          bankReference: payment.bankReference,
          qrTransactionId: payment.qrTransactionId,
          paymentNotes: payment.paymentNotes,
        })),
        exchangeReason: exchangeData.exchangeReason,
        exchangeNotes: exchangeData.exchangeNotes,
        ...(exchangeDateOverride != null ? { exchangeDate: exchangeDateOverride } : {}),
        ...(requiresZeroSaleAck ? { confirmZeroSale: true } : {}),
      };

      await salesService.processExchange(order.id, exchangePayload);

      setShowConfirmModal(false);
      onExchangeCompleted?.();
    } catch (err: unknown) {
      const errorMessage = extractErrorMessage(
        err,
        'No se pudo procesar el intercambio. Intente de nuevo.',
      );
      setShowConfirmModal(false);
      setSubmitError(errorMessage);
      console.error('Error processing exchange:', err);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    order,
    exchangeData,
    onExchangeCompleted,
    meetsExchangeCreditRule,
    getProductFinalPrice,
    isSubmitting,
    canModifySaleDate,
    exchangeDateMode,
    exchangeDate,
    requiresZeroSaleAck,
  ]);

  const formatCurrency = useCallback((amount: number) => formatCurrencyValue(amount), []);

  const handleUpdateExchangeNotes = useCallback((notes: string) => {
    setExchangeData((prev) => ({
      ...prev,
      exchangeNotes: notes,
    }));
  }, []);

  const handleDiscountToggle = useCallback((enabled: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      discountConfig: {
        ...getDiscountConfigOrDefault(prev.discountConfig),
        enabled,
        allowIndividualDiscounts: enabled
          ? (prev.discountConfig?.allowIndividualDiscounts ?? false)
          : false,
        orderDiscountType: 'AMOUNT',
        orderDiscountValue: 0,
      },
      newProducts: enabled
        ? prev.newProducts
        : prev.newProducts.map((product) => ({
            ...product,
            ...buildLinePricingFields(
              product.originalPrice,
              product.markupType,
              product.markupValue,
              'NONE',
              0,
            ),
          })),
    }));
  }, []);

  const handleMarkupToggle = useCallback((enabled: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      markupConfig: {
        ...getMarkupConfigOrDefault(prev.markupConfig),
        enabled,
        allowIndividualMarkups: enabled
          ? (prev.markupConfig?.allowIndividualMarkups ?? false)
          : false,
        orderMarkupType: 'AMOUNT',
        orderMarkupValue: 0,
      },
      newProducts: enabled
        ? prev.newProducts
        : prev.newProducts.map((product) => ({
            ...product,
            ...buildLinePricingFields(
              product.originalPrice,
              'NONE',
              0,
              product.discountType,
              product.discountValue,
            ),
          })),
    }));
  }, []);

  const handleIndividualMarkupToggle = useCallback((enabled: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      markupConfig: {
        ...getMarkupConfigOrDefault(prev.markupConfig),
        allowIndividualMarkups: enabled,
      },
    }));
  }, []);

  const handleIndividualDiscountToggle = useCallback(
    (enabled: boolean) => {
      setExchangeData((prev) => {
        const nextConfig = {
          ...getDiscountConfigOrDefault(prev.discountConfig),
          allowIndividualDiscounts: enabled,
        };

        if (enabled || !order) {
          return {
            ...prev,
            discountConfig: nextConfig,
          };
        }

        const originalItemsTotal = computeOriginalItemsTotal(order, prev.itemsToExchange);
        const floor = computePaidForExchangedItems(order, originalItemsTotal);
        const productsWithDiscount = mapExchangeProductsWithUniformDiscount(prev.newProducts, {
          discountType: nextConfig.orderDiscountType,
          discountValue: nextConfig.orderDiscountValue,
        });

        return {
          ...prev,
          discountConfig: nextConfig,
          newProducts: applyExchangeFinalPrices(productsWithDiscount, floor),
        };
      });
    },
    [order],
  );

  const handleUniformDiscountTypeChange = useCallback(
    (discountType: 'PERCENTAGE' | 'AMOUNT') => {
      applyUniformDiscountToNewProducts(discountType, 0);
    },
    [applyUniformDiscountToNewProducts],
  );

  const handleUniformDiscountValueChange = useCallback(
    (discountType: 'PERCENTAGE' | 'AMOUNT', value: number) => {
      if (!order) {
        setExchangeData((prev) => ({
          ...prev,
          discountConfig: {
            ...getDiscountConfigOrDefault(prev.discountConfig),
            orderDiscountType: discountType,
            orderDiscountValue: roundMoney(Math.max(0, value)),
          },
        }));
        return;
      }

      applyUniformDiscountToNewProducts(discountType, value);
    },
    [order, applyUniformDiscountToNewProducts],
  );

  const handleUniformMarkupTypeChange = useCallback(
    (markupType: 'PERCENTAGE' | 'AMOUNT') => {
      applyUniformMarkupToNewProducts(markupType, 0);
    },
    [applyUniformMarkupToNewProducts],
  );

  const handleUniformMarkupValueChange = useCallback(
    (markupType: 'PERCENTAGE' | 'AMOUNT', value: number) => {
      applyUniformMarkupToNewProducts(markupType, value);
    },
    [applyUniformMarkupToNewProducts],
  );

  const handlePartialPaymentToggle = useCallback((enabled: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      paymentConfig: {
        allowPartialPayment: enabled,
        allowMultiplePayments: prev.paymentConfig?.allowMultiplePayments ?? false,
      },
    }));
  }, []);

  const handleMultiplePaymentsToggle = useCallback((enabled: boolean) => {
    setExchangeData((prev) => ({
      ...prev,
      paymentConfig: {
        allowPartialPayment: prev.paymentConfig?.allowPartialPayment ?? false,
        allowMultiplePayments: enabled,
      },
      payments: enabled ? prev.payments : prev.payments?.slice(0, 1) || [],
    }));
  }, []);

  const clearError = useCallback(() => {
    setSubmitError(null);
    clearEligibilityError();
  }, [clearEligibilityError]);

  return {
    currentStep,
    isLoading,
    error,
    exchangeData,
    exchangeSummary,
    eligibility,
    paymentFlowPhase,
    setPaymentFlowPhase,
    paymentsLocked,
    setPaymentsLocked,
    clearExchangePayments,
    clearNewProducts,
    canProceedToNextStep,
    showConfirmModal,
    openConfirmModal,
    closeConfirmModal,
    requiresZeroSaleAck,
    handleNextStep,
    handlePreviousStep,
    handleSelectItemsToExchange,
    handleAddNewProduct,
    handleRemoveNewProduct,
    handleUpdateNewProduct,
    updateNewProductMarkup,
    updateNewProductDiscount,
    handleAddPayment,
    handleRemovePayment,
    handleUpdatePayment,
    handleUpdateExchangeNotes,
    resetAllLinePricing,
    commitCommercialPricingConfig,
    updateMarkupConfig,
    updateDiscountConfig,
    applyUniformMarkupToNewProducts,
    applyUniformDiscountToNewProducts,
    handleMarkupToggle,
    handleIndividualMarkupToggle,
    handleUniformMarkupTypeChange,
    handleUniformMarkupValueChange,
    handleDiscountToggle,
    handleIndividualDiscountToggle,
    handleUniformDiscountTypeChange,
    handleUniformDiscountValueChange,
    handlePartialPaymentToggle,
    handleMultiplePaymentsToggle,
    getProductFinalPrice,
    setProductDelivered,
    canModifySaleDate,
    exchangeDateMode,
    exchangeDate,
    exchangeDateModeError,
    handleExchangeDateModeChange,
    setExchangeDate,
    handleConfirmExchange,
    formatCurrency,
    clearError,
  };
};
